import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { FxAlert, FxButton, FxIcon, FxSpinner } from '@/components/fx'
import { llmApi } from '@/lib/api'
import type { ArmSummary, RecognitionArm } from '@/types/domain'
import { useCockpitContext } from '../useCockpit'
import { DialogShell } from './DialogShell'
import { COST_UNKNOWN, armLabel, costBound, formatCount, formatCost, isPromotable } from './format'

/** The arm that is frozen before any model runs, and therefore the undo. */
const BASELINE_ARM: RecognitionArm = 'ocr'

/**
 * "Lesungen vergleichen" — the same scans, read several ways, side by side.
 *
 * A template can hold the OCR baseline, the same pages read by one or two
 * models, and the reviewed values, all stored beside each other rather than
 * overwriting one another (`document_variants`). A comparison run changes
 * nothing; someone still has to decide afterwards, and adopting a reading
 * field by field would be thousands of clicks for a few hundred pages. So
 * the deciding happens here and the adopting is one call.
 *
 * The column that matters is **„anders als jetzt“**: coverage and cost say
 * what a reading *was*, that number says what accepting it would *do*.
 *
 * This dialog starts no runs. Starting one is the Dokumente tab's bulk bar,
 * where the selection is — and where `apply` has to be said out loud,
 * because the worker defaults it to true.
 */
export function ArmCompareDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const cockpit = useCockpitContext()
  const queryClient = useQueryClient()
  const templateId = cockpit.templateId
  const [confirming, setConfirming] = useState<ArmSummary | null>(null)

  const arms = useQuery({
    queryKey: ['llm', 'arms', templateId],
    queryFn: () => llmApi.arms(templateId),
    enabled: open && templateId.length > 0,
    staleTime: 30_000,
    retry: false,
  })

  const promote = useMutation({
    mutationFn: (arm: ArmSummary) =>
      llmApi.promote(templateId, {
        arm: arm.arm as RecognitionArm,
        ...(arm.model ? { model: arm.model } : {}),
      }),
    onSuccess: async () => {
      setConfirming(null)
      // Promoting rewrites the delivered values, so everything that renders
      // a value is stale: the documents list, the counts (review flags move)
      // and this dialog's own numbers.
      await Promise.all([
        cockpit.invalidate.documents(),
        cockpit.invalidate.counts(),
        cockpit.invalidate.template(),
        queryClient.invalidateQueries({ queryKey: ['llm', 'arms', templateId] }),
      ])
    },
  })

  const rows = arms.data ?? []
  const baselineAvailable = rows.some((arm) => arm.arm === BASELINE_ARM)

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      title="Lesungen vergleichen"
      description="Dieselben Scans, verschieden gelesen. „Anders als jetzt“ zeigt, was sich ändert, wenn Sie eine Lesung übernehmen."
      wide
    >
      {arms.isLoading ? (
        <FxSpinner label="Lesungen werden geladen …" />
      ) : arms.isError ? (
        <FxAlert tone="danger" title="Die Lesungen konnten nicht geladen werden">
          <FxButton variant="outlined" className="mt-4" onClick={() => void arms.refetch()}>
            Erneut versuchen
          </FxButton>
        </FxAlert>
      ) : rows.length === 0 ? (
        <p className="m-0 text-body text-text-muted">
          Für dieses Musterformular sind noch keine Lesungen gespeichert. Starten Sie eine
          KI-Prüfung über die Auswahl im Tab Dokumente — als Vergleichslauf ändert sie an den
          ausgelieferten Werten nichts.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {promote.isError && (
            <FxAlert tone="danger" title="Die Lesung konnte nicht übernommen werden">
              An den ausgelieferten Werten hat sich nichts geändert.
            </FxAlert>
          )}
          {promote.isSuccess && promote.data && (
            <FxAlert tone="success" title="Lesung übernommen">
              {formatCount(promote.data.promoted)} Dokumente liefern jetzt die Lesung „
              {armLabel(promote.data.arm)}“.
            </FxAlert>
          )}

          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {rows.map((arm) => (
              <ArmRow
                key={`${arm.arm}|${arm.model ?? ''}`}
                arm={arm}
                busy={promote.isPending}
                onPromote={() => setConfirming(arm)}
              />
            ))}
          </ul>

          <p className="m-0 text-small text-text-muted">
            {baselineAvailable
              ? 'Rückgängig: die Lesung „OCR“ übernehmen — sie wird vor jedem Modelllauf eingefroren und bleibt erhalten.'
              : 'Für dieses Musterformular ist noch keine OCR-Lesung gespeichert. Ohne sie gibt es hier keinen Weg zurück; übernehmen Sie eine Lesung erst, wenn Sie sie geprüft haben.'}{' '}
            Die geprüften Werte („geprüft“) lassen sich nicht übernehmen — sie entstehen beim
            Prüfen.
          </p>
        </div>
      )}

      {confirming && (
        <ConfirmPromote
          arm={confirming}
          baselineAvailable={baselineAvailable}
          busy={promote.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => promote.mutate(confirming)}
        />
      )}
    </DialogShell>
  )
}

function ArmRow({
  arm,
  busy,
  onPromote,
}: {
  arm: ArmSummary
  busy: boolean
  onPromote: () => void
}) {
  // Token and cost figures of a shared-cost arm are an upper bound, not this
  // arm's bill, and are marked as one — see `costBound`.
  const bound = costBound(arm)
  const numbers: string[] = [
    `${formatCount(arm.documents)} Dokumente`,
    ...(arm.fields > 0
      ? [
          `${formatCount(arm.changedFields)} von ${formatCount(arm.fields)} Feldern anders als jetzt`,
          `${formatCount(arm.changedDocuments)} Dokumente betroffen`,
        ]
      : []),
    ...(arm.needsReview > 0 ? [`${formatCount(arm.needsReview)} zu prüfen`] : []),
    `${bound}${formatCount(arm.inputTokens + arm.outputTokens)} Token`,
    // An unpriced model reports zero, which would read as "kostenlos".
    arm.priced ? `${bound}${formatCost(arm.cost, true)}` : `Kosten ${COST_UNKNOWN}`,
  ]

  return (
    <li className="flex flex-col gap-3 rounded-sm bg-ground p-4 sm:flex-row sm:items-start">
      <div className="min-w-0 flex-1">
        <p className="m-0 flex flex-wrap items-center gap-2">
          <span className="text-body-strong text-text">{armLabel(arm.arm)}</span>
          {arm.delivered && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-success-subtle px-2 py-0.5 text-small text-success-on-subtle">
              <FxIcon name="check_circle" size={16} />
              ausgeliefert
            </span>
          )}
        </p>
        <p className="m-0 mt-0.5 text-small text-text-muted">{arm.model || 'ohne Modell'}</p>
        <p className="m-0 mt-1 text-small text-text-muted">{numbers.join(' · ')}</p>
        {arm.costShared && (
          <p className="m-0 mt-1 text-small text-text-muted">
            Token und Kosten sind eine Obergrenze: Dieselben Aufrufe können auch einer anderen
            Lesung desselben Modells zugerechnet sein.
          </p>
        )}
      </div>
      {/* The action stays beside its numbers at every width. A table put it
          in the last column, where on a narrow window the one control this
          dialog exists for fell off the edge. */}
      {isPromotable(arm) && (
        <FxButton variant="tonal" disabled={busy} onClick={onPromote} className="shrink-0">
          Übernehmen
        </FxButton>
      )}
    </li>
  )
}

/**
 * Adopting a reading rewrites what every document delivers, so it asks
 * first — and says how much would change and how to get back, because those
 * are the two things a person can still judge before it happens.
 */
function ConfirmPromote({
  arm,
  baselineAvailable,
  busy,
  onCancel,
  onConfirm,
}: {
  arm: ArmSummary
  baselineAvailable: boolean
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <DialogShell open onClose={onCancel} title="Lesung übernehmen?">
      <div className="flex flex-col gap-4 text-body text-text">
        <p className="m-0">
          „{armLabel(arm.arm)}“{arm.model ? ` (${arm.model})` : ''} wird für{' '}
          {formatCount(arm.documents)} Dokumente die ausgelieferte Lesung.
        </p>
        <p className="m-0">
          {arm.fields > 0
            ? `${formatCount(arm.changedFields)} Felder in ${formatCount(arm.changedDocuments)} Dokumenten ändern sich.`
            : 'Wie viele Felder sich ändern, konnte nicht ermittelt werden.'}
        </p>
        <p className="m-0 text-small text-text-muted">
          {baselineAvailable
            ? 'Rückgängig machen Sie das, indem Sie hier die Lesung „OCR“ übernehmen — sie bleibt als Stand vor jedem Modelllauf erhalten. Geprüfte Werte bleiben unberührt.'
            : 'Achtung: Für dieses Musterformular ist keine OCR-Lesung gespeichert, mit der Sie zurückgehen könnten.'}
        </p>
        <div className="flex justify-end gap-3">
          <FxButton variant="outlined" onClick={onCancel} disabled={busy}>
            Abbrechen
          </FxButton>
          <FxButton onClick={onConfirm} disabled={busy}>
            {busy ? 'Wird übernommen …' : 'Übernehmen'}
          </FxButton>
        </div>
      </div>
    </DialogShell>
  )
}
